import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminHeaderAction,
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminSupportingText,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  AdminFormDropdownChevron,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import { buildSampleKeywords } from './keywordsDummyData';
import {
  FORM_MODE,
  ID_PLACEHOLDER,
  KEYWORD_ROUTE,
  SAVE_MESSAGES,
  buildCategoryKeywordForm,
  collectAddKeywordFieldErrors,
  collectCategoryEditKeywordFieldErrors,
  createInitialKeywordBlocks,
  emptyKeywordBlock,
  emptySubcategorySection,
  findKeywordListGroup,
  formatKeywordListDate,
  formatSubcategoryLabel,
  getKeywordCatalog,
  getSubcategoriesForTradeCategory,
  getTradeCategoryOptions,
  mergeKeywordsAfterBatchCreate,
  mergeKeywordsAfterCategoryEdit,
  removeOrClearKeywordBlock,
  resolveSubcategory,
  resolveTradeCategoryName,
  trimKeyword,
} from '../../../data/keywordWorkflow';

const ReadOnlyField = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <input
      type="text"
      value={value || '—'}
      readOnly
      className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
    />
  </div>
);

const AddLinkButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
  >
    <i className="fi fi-rs-plus text-xs" aria-hidden />
    {children}
  </button>
);

const KeywordBlock = ({
  block,
  index,
  labelPrefix = 'Keyword',
  isAdd,
  nameReadOnly = false,
  canRemove = false,
  error,
  onChange,
  onRemove,
}) => (
  <div className="rounded-xl border border-lightgray bg-white p-4 space-y-4">
    <div className="flex items-center justify-between gap-2">
      <AdminSupportingText className="mb-0 font-semibold !text-offblack">
        {labelPrefix} {index + 1}
      </AdminSupportingText>
      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
          aria-label={`Remove ${labelPrefix} ${index + 1}`}
        >
          <i className="fi fi-rs-trash text-sm" aria-hidden />
        </button>
      ) : null}
    </div>

    {!isAdd ? (
      <ReadOnlyField label="Keyword ID" value={block.id || ID_PLACEHOLDER} mono />
    ) : null}

    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        Keyword Name
        {!nameReadOnly ? <span className="text-alertred"> *</span> : null}
      </AdminDetailFieldLabel>
      {nameReadOnly ? (
        <input
          type="text"
          value={block.keyword || '—'}
          readOnly
          className={adminFormDetailInputClasses(true)}
        />
      ) : (
        <input
          type="text"
          placeholder="Enter Keyword Name"
          value={block.keyword || ''}
          onChange={(e) => onChange({ keyword: e.target.value })}
          className={adminFormDetailInputClasses(false, Boolean(error))}
        />
      )}
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  </div>
);

const KeywordDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tradeCategoryId: routeCategoryId } = useParams();

  const isAdd = /\/keywords\/add\/?$/.test(location.pathname);
  const isView = /\/view\/?$/.test(location.pathname);
  const isEdit = /\/edit\/?$/.test(location.pathname) && !isAdd;
  const mode = isAdd ? FORM_MODE.ADD : isView ? FORM_MODE.VIEW : FORM_MODE.EDIT;

  const allKeywords = useMemo(
    () => location.state?.keywords ?? buildSampleKeywords(),
    [location.state?.keywords],
  );

  const catalog = useMemo(() => getKeywordCatalog(), []);
  const tradeCategoryOptions = useMemo(() => getTradeCategoryOptions(catalog), [catalog]);

  const listGroup = useMemo(() => {
    if (isAdd) return null;
    return findKeywordListGroup(allKeywords, routeCategoryId);
  }, [allKeywords, isAdd, routeCategoryId]);

  const [form, setForm] = useState(() => {
    if (isAdd) {
      const tradeCategoryId = location.state?.tradeCategoryId || '';
      return {
        ...buildCategoryKeywordForm(
          null,
          tradeCategoryId,
          resolveTradeCategoryName(tradeCategoryId, catalog),
        ),
        poolBlocks: [],
        subcategorySections: [],
      };
    }
    return buildCategoryKeywordForm(listGroup);
  });
  const [baselineForm, setBaselineForm] = useState(() =>
    isAdd ? null : buildCategoryKeywordForm(listGroup),
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (isAdd) {
      const tradeCategoryId = location.state?.tradeCategoryId || '';
      setForm({
        ...buildCategoryKeywordForm(
          null,
          tradeCategoryId,
          resolveTradeCategoryName(tradeCategoryId, catalog),
        ),
        poolBlocks: [],
        subcategorySections: [],
      });
      setBaselineForm(null);
      setFieldErrors({});
      setSaveMessage('');
      return;
    }
    const next = buildCategoryKeywordForm(listGroup);
    setForm(next);
    setBaselineForm(next);
    setFieldErrors({});
    setSaveMessage('');
  }, [isAdd, listGroup, location.state?.tradeCategoryId, catalog]);

  const subcategoryOptions = useMemo(
    () => getSubcategoriesForTradeCategory(form.tradeCategoryId, catalog),
    [form.tradeCategoryId, catalog],
  );

  const pageHeading =
    mode === FORM_MODE.ADD
      ? 'Add Keywords'
      : mode === FORM_MODE.VIEW
        ? 'View Keywords'
        : 'Edit Keywords';

  const navigateToList = (nextKeywords, flashMessage) => {
    navigate(KEYWORD_ROUTE, {
      state: {
        keywords: nextKeywords,
        ...(flashMessage ? { flashMessage } : {}),
      },
    });
  };

  const clearFieldError = (...keys) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const handleTradeCategoryChange = (tradeCategoryId) => {
    setForm({
      tradeCategoryId,
      tradeCategoryName: resolveTradeCategoryName(tradeCategoryId, catalog),
      poolBlocks: [],
      subcategorySections: [],
      createdOn: '',
      lastUpdatedOn: '',
      contentStamp: '',
    });
    setFieldErrors({});
    setSaveMessage('');
  };

  const handleSectionSubcategoryChange = (sectionKey, subcategoryId) => {
    const sub = resolveSubcategory(form.tradeCategoryId, subcategoryId, catalog);
    clearFieldError(`sub-${sectionKey}`);
    setSaveMessage('');
    setForm((prev) => ({
      ...prev,
      subcategorySections: prev.subcategorySections.map((section) =>
        section.localKey === sectionKey
          ? {
              ...section,
              subcategoryId,
              subcategoryName: sub?.name || '',
              keywords: [],
            }
          : section,
      ),
    }));
  };

  const updatePoolBlock = (localKey, patch) => {
    clearFieldError(`pool-${localKey}`, '_form');
    setSaveMessage('');
    setForm((prev) => ({
      ...prev,
      poolBlocks: prev.poolBlocks.map((block) =>
        block.localKey === localKey ? { ...block, ...patch } : block,
      ),
    }));
  };

  const updateSectionKeyword = (sectionKey, localKey, patch) => {
    clearFieldError(`kw-${sectionKey}-${localKey}`, '_form');
    setSaveMessage('');
    setForm((prev) => ({
      ...prev,
      subcategorySections: prev.subcategorySections.map((section) =>
        section.localKey === sectionKey
          ? {
              ...section,
              keywords: section.keywords.map((block) =>
                block.localKey === localKey ? { ...block, ...patch } : block,
              ),
            }
          : section,
      ),
    }));
  };

  const addPoolKeywordBlock = () => {
    setForm((prev) => ({
      ...prev,
      poolBlocks: prev.poolBlocks.length
        ? [...prev.poolBlocks, emptyKeywordBlock()]
        : createInitialKeywordBlocks(1),
    }));
    clearFieldError('_form');
  };

  const removePoolBlock = (localKey) => {
    setForm((prev) => ({
      ...prev,
      poolBlocks: removeOrClearKeywordBlock(prev.poolBlocks, localKey),
    }));
    clearFieldError(`pool-${localKey}`, '_form');
  };

  const addSubcategorySection = () => {
    setForm((prev) => ({
      ...prev,
      subcategorySections: [...prev.subcategorySections, emptySubcategorySection()],
    }));
    clearFieldError('_form');
  };

  const removeSubcategorySection = (sectionKey) => {
    setForm((prev) => ({
      ...prev,
      subcategorySections: prev.subcategorySections.filter((s) => s.localKey !== sectionKey),
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.includes(sectionKey)) delete next[key];
      });
      return next;
    });
  };

  const addSubcategoryKeywordBlock = (sectionKey) => {
    setForm((prev) => ({
      ...prev,
      subcategorySections: prev.subcategorySections.map((section) =>
        section.localKey === sectionKey
          ? {
              ...section,
              keywords: section.keywords.length
                ? [...section.keywords, emptyKeywordBlock()]
                : createInitialKeywordBlocks(1),
            }
          : section,
      ),
    }));
    clearFieldError('_form');
  };

  const removeSectionKeyword = (sectionKey, localKey) => {
    setForm((prev) => ({
      ...prev,
      subcategorySections: prev.subcategorySections.map((section) =>
        section.localKey === sectionKey
          ? {
              ...section,
              keywords: removeOrClearKeywordBlock(section.keywords, localKey),
            }
          : section,
      ),
    }));
    clearFieldError(`kw-${sectionKey}-${localKey}`, '_form');
  };

  const handleSave = () => {
    if (isView) return;

    if (isAdd) {
      const errors = collectAddKeywordFieldErrors(
        {
          tradeCategoryId: form.tradeCategoryId,
          subcategorySections: form.subcategorySections,
          poolBlocks: form.poolBlocks,
        },
        allKeywords,
        catalog,
      );
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setSaveMessage(errors._form || SAVE_MESSAGES.failure);
        return;
      }

      const result = mergeKeywordsAfterBatchCreate(allKeywords, {
        tradeCategoryId: form.tradeCategoryId,
        tradeCategoryName:
          form.tradeCategoryName || resolveTradeCategoryName(form.tradeCategoryId, catalog),
        poolBlocks: form.poolBlocks,
        subcategorySections: form.subcategorySections.map((section) => ({
          ...section,
          blocks: section.keywords,
        })),
      });
      const flash =
        result.created.length > 1 ? SAVE_MESSAGES.createdBatch : SAVE_MESSAGES.createdSingle;
      navigateToList(result.keywords, flash);
      return;
    }

    const latestGroup = findKeywordListGroup(allKeywords, form.tradeCategoryId);
    const errors = collectCategoryEditKeywordFieldErrors(form, {
      existingKeywords: allKeywords,
      baselineForm,
      latestGroup,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSaveMessage(errors._form || SAVE_MESSAGES.updateFailure);
      return;
    }

    const result = mergeKeywordsAfterCategoryEdit(allKeywords, form, { baselineForm });
    navigateToList(result.keywords, result.changed ? SAVE_MESSAGES.updated : undefined);
  };

  if (!isAdd && !listGroup) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>{pageHeading}</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          No Keywords found for this Trade Category.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const nameReadOnly = isView;
  const showSave = isAdd || isEdit;
  const poolCount = form.poolBlocks.length;
  const subcategoryCount = form.subcategorySections.length;

  return (
    <AdminLayout>
      <div className="max-w-[25rem] mx-auto w-full min-w-0">
        <div className="mb-6">
          <AdminPageTitle className="!mb-0">{pageHeading}</AdminPageTitle>
        </div>

        {saveMessage ? (
          <div className="mb-6">
            <AdminFormFieldError className="mb-0">{saveMessage}</AdminFormFieldError>
          </div>
        ) : null}

        <div className="p-4 space-y-6 min-w-0">
          {isAdd ? (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Trade Category <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <div className="relative w-full">
                <select
                  value={form.tradeCategoryId || ''}
                  onChange={(e) => handleTradeCategoryChange(e.target.value)}
                  className={adminFormDetailSelectClasses(
                    false,
                    Boolean(fieldErrors.tradeCategoryId),
                  )}
                >
                  <option value="">Select Trade Category</option>
                  {tradeCategoryOptions.map((tc) => (
                    <option key={tc.id} value={tc.id}>
                      {tc.name}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
              {fieldErrors.tradeCategoryId ? (
                <AdminFormFieldError>{fieldErrors.tradeCategoryId}</AdminFormFieldError>
              ) : null}
            </div>
          ) : (
            <>
              <ReadOnlyField label="Trade ID" value={form.tradeCategoryId} mono />
              <ReadOnlyField label="Trade Category" value={form.tradeCategoryName} />
              <ReadOnlyField
                label="Created On"
                value={formatKeywordListDate(form.createdOn)}
              />
              <ReadOnlyField
                label="Last Updated On"
                value={formatKeywordListDate(form.lastUpdatedOn)}
              />
            </>
          )}

          {form.tradeCategoryId ? (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <AdminDetailFieldLabel className="!mb-0">Pool Keywords</AdminDetailFieldLabel>
                  <AdminSupportingText className="mb-0">
                    {poolCount} added
                  </AdminSupportingText>
                </div>

                {form.poolBlocks.length > 0 ? (
                  <div className="space-y-3">
                    {form.poolBlocks.map((block, index) => (
                      <KeywordBlock
                        key={block.localKey}
                        block={block}
                        index={index}
                        labelPrefix="Pool Keyword"
                        isAdd={isAdd}
                        nameReadOnly={nameReadOnly}
                        canRemove={isAdd && !block.id}
                        error={fieldErrors[`pool-${block.localKey}`]}
                        onChange={(patch) => updatePoolBlock(block.localKey, patch)}
                        onRemove={() => removePoolBlock(block.localKey)}
                      />
                    ))}
                  </div>
                ) : null}

                {isAdd ? (
                  <AddLinkButton onClick={addPoolKeywordBlock}>Add Pool Keywords</AddLinkButton>
                ) : null}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <AdminDetailFieldLabel className="!mb-0">Subcategories</AdminDetailFieldLabel>
                  <AdminSupportingText className="mb-0">
                    {subcategoryCount} added
                  </AdminSupportingText>
                </div>

                {fieldErrors._form && !saveMessage ? (
                  <AdminFormFieldError>{fieldErrors._form}</AdminFormFieldError>
                ) : null}

                <div className="space-y-3">
                  {form.subcategorySections.map((section, sectionIndex) => {
                    const selectedIds = form.subcategorySections
                      .filter((s) => s.localKey !== section.localKey && s.subcategoryId)
                      .map((s) => s.subcategoryId);
                    const showSubRequired =
                      isAdd &&
                      (section.keywords || []).some(
                        (block) =>
                          trimKeyword(block.keyword) || String(block.keyword || '').length > 0,
                      );
                    const keywordCount = (section.keywords || []).length;

                    return (
                      <div
                        key={section.localKey}
                        className="rounded-xl border border-lightgray bg-white p-4 space-y-4"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <AdminSupportingText className="mb-0 font-semibold !text-offblack">
                            Subcategory {sectionIndex + 1}
                          </AdminSupportingText>
                          {isAdd ? (
                            <button
                              type="button"
                              onClick={() => removeSubcategorySection(section.localKey)}
                              className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                              aria-label={`Remove Subcategory ${sectionIndex + 1}`}
                            >
                              <i className="fi fi-rs-trash text-sm" aria-hidden />
                            </button>
                          ) : null}
                        </div>

                        {isAdd ? (
                          <div className="space-y-1 w-full min-w-0">
                            <AdminDetailFieldLabel>
                              Subcategory
                              {showSubRequired ? (
                                <span className="text-alertred"> *</span>
                              ) : null}
                            </AdminDetailFieldLabel>
                            <div className="relative w-full">
                              <select
                                value={section.subcategoryId || ''}
                                onChange={(e) =>
                                  handleSectionSubcategoryChange(
                                    section.localKey,
                                    e.target.value,
                                  )
                                }
                                className={adminFormDetailSelectClasses(
                                  false,
                                  Boolean(fieldErrors[`sub-${section.localKey}`]),
                                )}
                              >
                                <option value="">Select Subcategory</option>
                                {subcategoryOptions.map((sub) => (
                                  <option
                                    key={sub.id}
                                    value={sub.id}
                                    disabled={selectedIds.includes(sub.id)}
                                  >
                                    {formatSubcategoryLabel(sub.id, sub.name)}
                                  </option>
                                ))}
                              </select>
                              <AdminFormDropdownChevron />
                            </div>
                            {fieldErrors[`sub-${section.localKey}`] ? (
                              <AdminFormFieldError>
                                {fieldErrors[`sub-${section.localKey}`]}
                              </AdminFormFieldError>
                            ) : null}
                          </div>
                        ) : (
                          <>
                            <ReadOnlyField
                              label="Subcategory ID"
                              value={section.subcategoryId}
                              mono
                            />
                            <ReadOnlyField
                              label="Subcategory"
                              value={formatSubcategoryLabel(
                                section.subcategoryId,
                                section.subcategoryName,
                              )}
                            />
                          </>
                        )}

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <AdminDetailFieldLabel className="!mb-0">
                              Subcategory Keywords
                            </AdminDetailFieldLabel>
                            <AdminSupportingText className="mb-0">
                              {keywordCount} added
                            </AdminSupportingText>
                          </div>

                          {(section.keywords || []).length > 0 ? (
                            <div className="space-y-3">
                              {(section.keywords || []).map((block, index) => (
                                <KeywordBlock
                                  key={block.localKey}
                                  block={block}
                                  index={index}
                                  labelPrefix="Keyword"
                                  isAdd={isAdd}
                                  nameReadOnly={nameReadOnly}
                                  canRemove={isAdd && !block.id}
                                  error={
                                    fieldErrors[`kw-${section.localKey}-${block.localKey}`]
                                  }
                                  onChange={(patch) =>
                                    updateSectionKeyword(
                                      section.localKey,
                                      block.localKey,
                                      patch,
                                    )
                                  }
                                  onRemove={() =>
                                    removeSectionKeyword(section.localKey, block.localKey)
                                  }
                                />
                              ))}
                            </div>
                          ) : null}

                          {isAdd && section.subcategoryId ? (
                            <AddLinkButton
                              onClick={() => addSubcategoryKeywordBlock(section.localKey)}
                            >
                              Add Subcategory Keywords
                            </AddLinkButton>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {isAdd ? (
                  <AddLinkButton onClick={addSubcategorySection}>Add Subcategory</AddLinkButton>
                ) : null}
              </div>
            </>
          ) : null}

          {showSave ? (
            <div className="flex items-center justify-end gap-3">
              <AdminHeaderAction size="lg" type="button" onClick={handleSave}>
                Save
              </AdminHeaderAction>
            </div>
          ) : null}
        </div>
      </div>
    </AdminLayout>
  );
};

export default KeywordDetail;
